/**
 * SubmissionModal — lets a user enter a competition. Renders fields based on
 * the competition_type: catch (weight/lake/photo), photo (photo+caption),
 * community (title+description). Validates client-side, posts to
 * /api/competitions/{id}/submissions and shows the trial/badge celebration toast.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Camera, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SubmissionModal = ({ competition, open, onClose, onSuccess, token }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    image_url: '',
    weight: '',
    lake_name: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!open || !competition) return null;

  const isCatchType = competition.competition_type === 'catch';
  const requiresPhoto = isCatchType || competition.competition_type === 'photo';

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large — max 10MB', { duration: 6000 });
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file', { duration: 6000 });
      return;
    }
    // Resize + compress client-side and store as a base64 data URI directly
    // on the submission. This matches the Community catch flow and avoids the
    // ephemeral-filesystem persistence problem that was wiping uploads on every
    // redeploy. Backend's `image_url` field stores the string as-is.
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Use document.createElement instead of `new Image()` — the capitalised
      // global confuses Emergent's visual-edits babel plugin into thinking
      // Image is a React component reference and crashes on cross-file lookup.
      const img = document.createElement('img');
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = Math.min(MAX / width, MAX / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const dataUri = canvas.toDataURL('image/jpeg', 0.82);
        setForm((f) => ({ ...f, image_url: dataUri }));
        setUploading(false);
        toast.success('Photo attached', { duration: 3000 });
      };
      img.onerror = () => {
        setUploading(false);
        toast.error('Could not read that photo. Try a different image.', { duration: 6000 });
      };
      img.src = ev.target.result;
    };
    reader.onerror = () => {
      setUploading(false);
      toast.error('Could not read that photo.', { duration: 6000 });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Add a title for your entry');
      return;
    }
    if (requiresPhoto && !form.image_url) {
      toast.error('A photo is required for this competition');
      return;
    }
    if (isCatchType && !form.weight) {
      toast.error('Catch weight is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        image_url: form.image_url || null,
        weight: form.weight ? Number(form.weight) : null,
        lake_name: form.lake_name.trim() || null,
      };
      const r = await axios.post(
        `${API}/competitions/${competition.id}/submissions`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.status === 'approved') {
        toast.success('Your entry is live — share the love and vote!', { duration: 6000 });
      } else {
        toast.success('Entry submitted — pending admin review.', { duration: 6000 });
      }
      setForm({ title: '', description: '', image_url: '', weight: '', lake_name: '' });
      onSuccess?.(r.data);
      onClose();
    } catch (err) {
      console.error('Submission error:', err);
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string'
        ? d
        : d?.message
          || (err.response?.status ? `Server responded ${err.response.status}` : 'Network error — check connection and try again.');
      toast.error(`Couldn't submit entry: ${msg}`, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="submission-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="glass-card max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 border-[#c8b273]/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="data-label text-[#c8b273] mb-1">Enter Competition</p>
            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
              {competition.title}
            </h3>
            <p className="text-xs text-[#8a948a] mt-1">
              Prize: {competition.prize_summary || '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[#8a948a] hover:text-white rounded transition-colors"
            data-testid="submission-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#8a948a] mb-1.5">
              Entry Title *
            </label>
            <Input
              data-testid="submission-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={isCatchType ? 'e.g. 28lb mirror at dawn' : 'Give your entry a title'}
              maxLength={120}
              className="bg-black/30 border-white/10 text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#8a948a] mb-1.5">
              Description {competition.competition_type === 'community' && '*'}
            </label>
            <Textarea
              data-testid="submission-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="The story behind the entry — bait, rig, swim, time of day…"
              rows={3}
              className="bg-black/30 border-white/10 text-white"
            />
          </div>

          {/* Photo */}
          {requiresPhoto && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#8a948a] mb-1.5">
                Photo *
              </label>
              {form.image_url ? (
                <div className="relative rounded overflow-hidden border border-[#c8b273]/30">
                  <img src={form.image_url} alt="Entry preview" className="w-full max-h-64 object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: '' })}
                    className="absolute top-2 right-2 bg-black/70 text-white p-1 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="submission-image-file"
                  className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-white/10 rounded cursor-pointer hover:border-[#c8b273]/40 transition-colors"
                  data-testid="submission-image-upload"
                >
                  {uploading ? (
                    <p className="text-sm text-[#8a948a]">Uploading…</p>
                  ) : (
                    <>
                      <Camera className="h-6 w-6 text-[#c8b273]" />
                      <p className="text-sm text-[#e2e4e2]">Tap to add photo</p>
                      <p className="text-xs text-[#6b7d6b]">JPG/PNG up to 8MB</p>
                    </>
                  )}
                  <input
                    id="submission-image-file"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          )}

          {/* Catch-specific: weight + lake */}
          {isCatchType && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-[#8a948a] mb-1.5">
                  Weight (lb) *
                </label>
                <Input
                  data-testid="submission-weight"
                  type="number"
                  step="0.1"
                  min="0"
                  value={form.weight}
                  onChange={(e) => setForm({ ...form, weight: e.target.value })}
                  placeholder="28.5"
                  className="bg-black/30 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-[#8a948a] mb-1.5">
                  Lake Name
                </label>
                <Input
                  data-testid="submission-lake"
                  value={form.lake_name}
                  onChange={(e) => setForm({ ...form, lake_name: e.target.value })}
                  placeholder="e.g. Linear Fisheries"
                  className="bg-black/30 border-white/10 text-white"
                />
              </div>
            </div>
          )}

          {/* Rules reminder */}
          {competition.rules && (
            <div className="text-xs text-[#8a948a] p-3 bg-[#0b0d0b] rounded border border-white/5">
              <span className="font-bold text-[#c8b273] uppercase tracking-widest mr-1">Rules:</span>
              {competition.rules}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              className="btn-gold flex-1"
              disabled={submitting || uploading}
              data-testid="submission-submit-btn"
            >
              {submitting ? 'Submitting…' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Entry
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-[#8a948a]"
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmissionModal;
